"use client";

import { useState, useEffect, useMemo } from "react";
import { Search, Edit2, Trash2, Plus, Folder } from "lucide-react";
import { CategoryModal } from "../../components/categories/CategoryModal";
import { DeleteConfirmModal } from "../../components/categories/DeleteConfirmModal";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory
} from "../../services/CategoriesApi";
import { Category } from "../../types/categories";
import PageHeader from "../../components/ui/PageHeader";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../auth/AuthContext";

export default function CategoriesPageNew() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryErrors, setCategoryErrors] = useState<{ name?: string; colorCode?: string }>({});
  const { user } = useAuth();
  const toast = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // --- Modal state ---
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryMode, setCategoryMode] = useState<"add" | "edit">("add");
  const [editingCategory, setEditingCategory] = useState<any>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "category";
    id: string;
    name: string;
  } | null>(null);

  // Helper to get location_id from user context
  const getLocationId = (): number => {
    const toNumber = (
      ...values: Array<string | number | undefined | null>
    ): number | undefined => {
      for (const value of values) {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string" && value.trim() !== "") {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) return parsed;
        }
      }
      return undefined;
    };
    return (
      toNumber(
        user?.location_id,
        user?.location?.id,
        user?.locationId,
        user?.default_location_id
      ) ?? 1
    );
  };

  // --- Category Handlers ---
  const handleCategoryAdd = () => {
    setCategoryMode("add");
    setEditingCategory(null);
    setCategoryErrors({});
    setShowCategoryModal(true);
  };

  const handleCategoryEdit = (category: Category) => {
    setCategoryMode("edit");
    setEditingCategory(category);
    setCategoryErrors({});
    setShowCategoryModal(true);
  };

  const handleCategoryDelete = (id: string, name: string) => {
    setDeleteTarget({ type: "category", id, name });
    setShowDeleteModal(true);
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetched = await getCategories();
      setCategories(fetched);
    } catch (err: any) {
      console.error("Failed to fetch categories:", err);
      setError(err.response?.data?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // --- Filtered categories ---
  const filteredCategories = useMemo(() => {
    return categories.filter((c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  // Paginated categories
  const paginatedCategories = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCategories.slice(startIndex, endIndex);
  }, [filteredCategories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full ">
        {/* Header */}
        <PageHeader
          title="Categories"
          description="Organize your services into categories"
          icon={Folder}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            <button
              onClick={handleCategoryAdd}
              className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              <span>Add Category</span>
            </button>
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
          {/* Left: Per Page Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[80px] bg-white border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
          </div>

          {/* Right: Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Main Content - Categories Table */}
        <div className="flex-1 pb-6">
          <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
            <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
                  <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
                    <th className="col-span-5 text-left font-bold">Name</th>
                    <th className="col-span-5 text-left font-bold">Status</th>
                    <th className="col-span-2 text-right font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
                  {loading ? (
                    <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                      <td colSpan={12} className="h-full">
                        <div className="flex h-full w-full items-center justify-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                      <td colSpan={12} className="h-full">
                        <div className="flex flex-col h-full w-full items-center justify-center text-center px-6">
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            Unable to load categories
                          </p>
                          <p className="text-gray-500 mb-6">{error}</p>
                          <button
                            onClick={fetchCategories}
                            className="px-6 py-2 rounded-lg bg-[#885ABB] text-white font-medium hover:bg-[#6f47a2]"
                          >
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedCategories.length === 0 ? (
                    <tr className="flex flex-col h-64 text-gray-400 w-full lg:grid lg:grid-cols-12">
                      <td className="col-span-12 flex flex-col items-center justify-center w-full h-full">
                        <Search className="w-12 h-12 mb-3 opacity-20 mx-auto" />
                        <p className="text-lg font-medium text-gray-500">
                          No categories found
                        </p>
                        <p className="text-sm">
                          {searchTerm
                            ? "Try adjusting your search"
                            : "Start by adding your first category"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {paginatedCategories.map((category, index) => (
                        <tr
                          key={category.id}
                          className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full relative"
                        >
                          <td className="lg:col-span-5 font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors text-base lg:text-sm break-words">
                            {category.name}
                          </td>

                          <td className="lg:col-span-5 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                            <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Status</span>
                            <span
                              className={cn(
                                "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                                category.status === "active"
                                  ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]"
                                  : "bg-gray-100 text-gray-600 border-gray-200"
                              )}
                            >
                              {category.status === "active" ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td className="lg:col-span-2 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCategoryEdit(category)}
                              className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                              title="Edit category"
                            >
                              <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                              <span className="lg:hidden">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleCategoryDelete(category.id, category.name)
                              }
                              className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                              title="Delete category"
                            >
                              <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                              <span className="lg:hidden">Delete</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 lg:px-6 py-4 mt-4 border-t border-gray-100 bg-white rounded-xl shadow-sm">
              <div className="text-[10px] lg:text-sm text-gray-500 font-bold lg:font-medium uppercase lg:normal-case tracking-wider lg:tracking-normal text-center sm:text-left order-2 sm:order-1">
                Showing <span className="text-[#885ABB]">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="text-[#885ABB]">{Math.min(currentPage * itemsPerPage, filteredCategories.length)}</span> of <span className="text-[#885ABB] font-black">{filteredCategories.length}</span> categories
              </div>
              <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex-1 sm:flex-none px-4 py-2.5 lg:py-2 border border-gray-200 rounded-xl text-xs lg:text-sm font-bold text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap shadow-sm"
                >
                  Previous
                </button>
                <div className="px-4 py-2.5 lg:py-2 bg-purple-50 rounded-xl border border-purple-100 h-full flex items-center justify-center min-w-[100px]">
                  <span className="text-[10px] lg:text-sm font-bold text-[#885ABB] whitespace-nowrap">
                    Page {currentPage} / {totalPages}
                  </span>
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex-1 sm:flex-none px-4 py-2.5 lg:py-2 border border-gray-200 rounded-xl text-xs lg:text-sm font-bold text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap shadow-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          isOpen={showCategoryModal}
          mode={categoryMode}
          category={editingCategory}
          saving={saving}
          serverErrors={categoryErrors}
          onClose={() => {
            if (!saving) {
              setShowCategoryModal(false);
            }
          }}
          onSave={async (data) => {
            try {
              setSaving(true);
              setCategoryErrors({});

              if (categoryMode === "add") {
                // Create new category
                const newCategory = await createCategory({
                  name: data.name,
                  slug: data.slug,
                  color_code: data.color_code || "#885ABB",
                  status: "active",
                });
                setCategories([...categories, newCategory]);
              } else {
                // Update existing category
                const updated = await updateCategory(editingCategory!.id, {
                  name: data.name,
                  slug: data.slug,
                  color_code: data.color_code || editingCategory!.color_code || "#885ABB",
                  status: editingCategory!.status,
                });
                setCategories(
                  categories.map((c) =>
                    c.id === editingCategory!.id ? updated : c
                  )
                );
              }
              toast.success(`Category ${categoryMode === "add" ? "added" : "updated"} successfully!`);
              setShowCategoryModal(false);
              setCategoryErrors({});
            } catch (err: any) {
              console.error("Failed to save category:", err);
              if (err.response?.status === 422 && err.response?.data?.errors) {
                const bErrors = err.response.data.errors;
                setCategoryErrors({
                  name: bErrors.name ? bErrors.name[0] : undefined,
                  colorCode: bErrors.color_code ? bErrors.color_code[0] : undefined,
                });
                toast.error("Please fix the errors below.");
              } else {
                toast.error(err.response?.data?.message || "Failed to save category. Please try again.");
              }
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {showDeleteModal && deleteTarget && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          type={deleteTarget.type}
          name={deleteTarget.name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            try {
              if (deleteTarget.type === "category") {
                await deleteCategory(deleteTarget.id);
                setCategories(categories.filter((c) => c.id !== deleteTarget.id));
              }
              toast.success("Category deleted successfully!");
              setShowDeleteModal(false);
            } catch (err: any) {
              console.error("Failed to delete category:", err);
              toast.error(err.response?.data?.message || "Failed to delete category. Please try again.");
            }
          }}
        />
      )}
    </div>
  );
}
